// src/pages/AboutUsPage.jsx
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AboutUsPage = () => {
  const navigate = useNavigate();

  // Ensure page loads at the top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-6 px-4 md:px-8 max-w-5xl mx-auto w-full min-h-[70vh] animate-fade-in">
      
      {/* Header */}
      <div className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-5">
        <button 
          onClick={() => navigate(-1)} 
          className="w-10 h-10 rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600 transition-colors"
        >
          <i className="fas fa-arrow-left"></i>
        </button>
        <h1 className="text-2xl md:text-3xl font-black text-[#0F2F63] flex items-center gap-3">
          <i className="fas fa-info-circle text-[#FF7A59]"></i> About Us
        </h1>
      </div>

      {/* Hero Image Section */}
      <div className="w-full h-64 md:h-80 rounded-3xl overflow-hidden mb-8 shadow-sm relative">
        <img 
          src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=1200&q=80" 
          alt="SV Mart Fresh Groceries" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2F63]/90 to-transparent flex items-end p-8">
          <div className="text-white">
            <h2 className="text-3xl md:text-4xl font-black mb-2">Welcome to SV MART</h2>
            <p className="text-white/90 text-lg font-medium max-w-2xl">Delivering freshness, quality, and convenience right to your doorstep.</p>
          </div>
        </div>
      </div>

      {/* Mission & Vision */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
          <div className="w-14 h-14 bg-sky-50 rounded-full flex items-center justify-center mb-6">
            <i className="fas fa-bullseye text-2xl text-sky-600"></i>
          </div>
          <h3 className="text-xl font-black text-gray-900 mb-3">Our Mission</h3>
          <p className="text-gray-600 font-medium leading-relaxed">
            At SV MART, our mission is to simplify your everyday life by providing a seamless, fast, and highly reliable shopping experience. We aim to connect you with the highest quality groceries, fresh produce, and household essentials at unbeatable prices.
          </p>
        </div>
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100">
          <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mb-6">
            <i className="fas fa-eye text-2xl text-[#2CCE75]"></i>
          </div>
          <h3 className="text-xl font-black text-gray-900 mb-3">Our Vision</h3>
          <p className="text-gray-600 font-medium leading-relaxed">
            We envision a future where grocery shopping is no longer a chore but a delightful experience. By leveraging cutting-edge technology and a robust delivery network, we strive to become the most trusted e-commerce platform in the country.
          </p>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="mb-12">
        <h3 className="text-2xl font-black text-center text-[#0F2F63] mb-8">Why Choose SV MART?</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-bolt text-2xl text-[#FF7A59]"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Lightning Fast</h4>
            <p className="text-sm text-gray-500 font-medium">Get your essentials delivered in minutes with our Quick Delivery option.</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-leaf text-2xl text-[#2CCE75]"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Fresh Quality</h4>
            <p className="text-sm text-gray-500 font-medium">We source our products directly from trusted farmers and top reliable brands.</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-sky-50 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-tags text-2xl text-sky-600"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Best Prices</h4>
            <p className="text-sm text-gray-500 font-medium">Enjoy incredible daily discounts, special combo offers, and budget-friendly rates.</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-purple-50 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-shield-alt text-2xl text-purple-600"></i>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mb-2">Secure Platform</h4>
            <p className="text-sm text-gray-500 font-medium">Your data and transactions are completely protected with top-tier security standards.</p>
          </div>

        </div>
      </div>

      {/* Bottom Image / Story */}
      <div className="bg-[#0F2F63] rounded-3xl overflow-hidden flex flex-col md:flex-row items-center">
        <div className="w-full md:w-1/2 p-8 md:p-12 text-white">
          <h3 className="text-2xl md:text-3xl font-black mb-4">Our Story</h3>
          <p className="text-white/80 leading-relaxed font-medium mb-6">
            What started as a small initiative to bring daily conveniences closer to home has quickly transformed into a robust e-commerce ecosystem. With thousands of happy customers, we continue to grow every single day, keeping our core values of trust, speed, and affordability intact.
          </p>
          <button onClick={() => navigate('/quick')} className="bg-[#FF7A59] hover:bg-orange-600 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-md">
            Start Shopping
          </button>
        </div>
        <div className="w-full md:w-1/2 h-64 md:h-full min-h-[300px]">
          <img 
            src="https://images.unsplash.com/photo-1583258292688-d0213dc5a3a8?w=800&q=80" 
            alt="SV Mart Delivery" 
            className="w-full h-full object-cover"
          />
        </div>
      </div>

    </div>
  );
};

export default AboutUsPage;